import { DeadlineSubject, NeedStatus } from "../../autogen/bff-api";
import { sourcingEventForModel } from "./sourcingEventForModel";
import { contractForModel } from "./contractForModel";
import type { ContractNature, SourcingEventStatus } from "../../autogen/bff-api";
import { urls } from "../../urls";
import { STATUS_BUCKET, STATUS_FILTER_PARAM } from "../layout/statusBuckets";
import { base64Encode } from "../utils/base64";
import { contractListFilters } from "./contractReportQuery";
import { PLANNER_RESULT_MAX_CHARS } from "./tools";
import type { ToolResult } from "./tools";

export type ListLink = { tool: string; path: string; total: number; shown: number };

type Row = Record<string, unknown>;

export type StatusLabel = (toolName: string, status: string) => string;

type ListTool = {
  items: (data: Row) => unknown;
  total: (data: Row) => unknown;
  row: (item: Row, status: (value: unknown) => unknown) => Row;
  path?: (args: Row) => string;
  note?: (data: Row) => string | undefined;
};

const LIST_TOOLS: Record<string, ListTool> = {
  listBuyerEvents: {
    items: (data) => data.events,
    total: (data) => data.count,
    row: (event, status) => ({
      id: event.id,
      title: event.title,
      status: status(event.publicStatus),
      deadline: event.deadline,
      responsible: personName(event.responsible),
      contractType: event.contractNature,
      referenceId: event.referenceId,
    }),
    path: (args) =>
      withParams(urls.app.sourcingEvents.viewAll, {
        name: text(args.name),
        deadline: args.deadlinePassed === true || args.deadlinePassed === "true" ? "passed" : undefined,
        [STATUS_FILTER_PARAM]: joined(statusBuckets(list(args.statuses))),
        responsible: joined(list(args.responsiblePersons)),
        contractType: joined(list(args.contractTypes) as ContractNature[]),
      }),
  },
  listContractReports: {
    items: (data) => data.reports,
    total: (data) => data.count,
    row: (report, status) => ({
      contractId: report.contractId,
      title: report.title,
      counterparty: report.counterpartyName,
      counterpartyId: report.counterpartyId,
      status: status(report.status),
      startDate: report.startDate,
      endDate: report.endDate,
      value: report.totalPrice ?? report.estimatedValue,
      currency: report.currency,
    }),
    path: (args) => {
      const filters = contractListFilters(args);
      return withParams(urls.app.contracts.viewAll, {
        q: Object.keys(filters).length > 0 ? base64Encode(JSON.stringify(filters)) : undefined,
      });
    },
  },
  getProjects: {
    items: (data) => data.projects,
    total: (data) => data.count,
    row: (project, status) => ({
      id: project.id,
      title: project.name,
      status: status(project.status),
      startDate: project.startDate,
      endDate: project.endDate,
      responsible: personName(project.projectResponsible),
    }),
    path: (args) =>
      withParams(urls.app.projects.viewAll, { name: text(args.name), status: joined(list(args.status)) }),
  },
  listNeeds: {
    items: (data) => data.needs,
    total: (data) => data.count,
    row: (need, status) => ({
      id: need.id,
      title: need.title,
      status: status(need.status),
      deliveryDate: need.deliveryDate,
      value: need.estimatedValue,
      department: (need.department as Row | null | undefined)?.name,
      procurement: (need.procurement as Row | null | undefined)?.title,
    }),
    path: (args) =>
      withParams(urls.app.needs.list, {
        status: joined(list(args.status).map((status) => status.toUpperCase()).filter(isNeedStatus)),
      }),
  },
  listMyUpcomingDeadlines: {
    items: (data) => data.deadlines,
    total: (data) => (Array.isArray(data.deadlines) ? data.deadlines.length : undefined),
    note: (data) => joinedNotes(deadlinesCutShortNote(data), contractTasksNote(data.deadlines)),
    row: (deadline) =>
      deadline.subject === DeadlineSubject.ContractTask
        ? {
            id: deadline.subjectId,
            title: deadline.subjectTitle,
            kind: deadline.kind,
            dueAt: toMinute(deadline.dueAt),
            contractId: deadline.parentId,
            contract: deadline.detail,
            link: entityLink(deadline.subjectTitle, deadline.subjectId),
            contractLink: entityLink(deadline.detail, deadline.parentId),
          }
        : {
            id: deadline.subjectId,
            title: deadline.subjectTitle,
            kind: deadline.kind,
            dueAt: toMinute(deadline.dueAt),
            detail: deadline.detail,
          },
    path: () => urls.app.home,
  },
  searchOrganizationsAndEntries: {
    items: (data) => data.organizations,
    total: (data) => data.count,
    row: (organization) => ({
      id: organization.id,
      name: organization.name,
      type: organization.type,
      organizationNumber: organization.organizationNumber,
    }),
  },
};

export type ListView = { total: number; shown: number; rows: Row[]; note: string };

export const isListTool = (toolName: string): boolean => toolName in LIST_TOOLS;

export const listView = (
  toolName: string,
  result: ToolResult,
  statusLabel: StatusLabel = (_, status) => status,
): ListView | null => {
  const tool = LIST_TOOLS[toolName];
  if (!tool || !result.ok || !isRow(result.data)) return null;
  const items = tool.items(result.data);
  if (!Array.isArray(items)) return null;
  const status = (value: unknown) => (typeof value === "string" && value ? statusLabel(toolName, value) : value);
  const projected = items.filter(isRow).map((item) => tool.row(item, status)).map(withoutEmpty);
  const reported = tool.total(result.data);
  const total = typeof reported === "number" ? Math.max(reported, projected.length) : projected.length;
  const rows = fitToBudget(projected, total);
  const cutShort = tool.note?.(result.data);
  const note = cutShort ? `${noteFor(total, rows.length)} ${cutShort}` : noteFor(total, rows.length);
  return { total, shown: rows.length, rows, note };
};

const SOURCING_EVENT_RESULT_TOOLS = new Set(["getSourcingEvent", "updateSourcingEvent"]);

export const resultForModel = (toolName: string, result: ToolResult, statusLabel?: StatusLabel): ToolResult => {
  if (SOURCING_EVENT_RESULT_TOOLS.has(toolName) && result.ok) return { ok: true, data: sourcingEventForModel(result.data) };
  if (toolName === "getContract" && result.ok) return { ok: true, data: contractForModel(result.data) };
  const view = listView(toolName, result, statusLabel);
  return view ? { ok: true, data: view } : result;
};

export const listLink = (toolName: string, args: Row, result: ToolResult): ListLink | null => {
  const path = LIST_TOOLS[toolName]?.path;
  const view = listView(toolName, result);
  if (!path || !view) return null;
  return { tool: toolName, path: path(args), total: view.total, shown: view.shown };
};

const entityLink = (name: unknown, id: unknown): string | undefined =>
  typeof name === "string" && name && typeof id === "string" && id ? `[${name}](#entity:${id})` : undefined;

const joinedNotes = (...notes: (string | undefined)[]): string | undefined =>
  notes.filter((note): note is string => !!note).join(" ") || undefined;

const deadlinesCutShortNote = (data: Row): string | undefined =>
  typeof data.upcomingCutShortAt === "string"
    ? `Lista stoppet ved grensen og når bare fram til ${toMinute(data.upcomingCutShortAt)}. Si i svaret at du ` +
      "bare har sett fristene fram til denne datoen, at det kan finnes flere etter den, og vis til lenken under " +
      "svaret. Si ALDRI at en type frist mangler i perioden: frister etter denne datoen har du ikke sett."
    : undefined;

const deadlineContract = (deadline: Row): { id: unknown; name: unknown } | null =>
  deadline.subject === DeadlineSubject.ContractTask ? { id: deadline.parentId, name: deadline.detail }
  : deadline.subject === DeadlineSubject.Contract ? { id: deadline.subjectId, name: deadline.subjectTitle }
  : null;

const contractTasksNote = (deadlines: unknown): string | undefined => {
  if (!Array.isArray(deadlines)) return undefined;
  const rows = deadlines.filter(isRow);
  if (!rows.some((deadline) => deadline.subject === DeadlineSubject.ContractTask)) return undefined;
  const taskRule =
    "Skriv navnet på HVER kontraktsoppgave som lenken i radens link, i tabellen og i teksten, så brukeren kommer " +
    "rett til oppgaven.";
  const contracts = rows.map(deadlineContract);
  const only = contracts[0];
  const oneContract =
    rows.length > 1 && only && contracts.every((contract) => contract?.id === only.id) ? entityLink(only.name, only.id) : undefined;
  return oneContract
    ? `${taskRule} Alle fristene hører til samme kontrakt: nevn den én gang, som ${oneContract}, over tabellen, ` +
        "og ha IKKE en kolonne for kontrakten."
    : `${taskRule} Skriv kontrakten oppgaven hører til med radens contractLink.`;
};

const noteFor = (total: number, shown: number): string => {
  if (total === 0) return "Ingen treff. Si det rett ut til brukeren.";
  if (shown === total) return `Alle ${total} vises.`;
  return (
    `Viser ${shown} av ${total}. Si begge tallene i svaret. Vil brukeren snevre inn, bruk filter i et nytt kall; ` +
    "ikke hent flere sider for å lete selv."
  );
};

const fitToBudget = (rows: Row[], total: number): Row[] => {
  let fits = 0;
  let tooMany = rows.length + 1;
  while (tooMany - fits > 1) {
    const tried = Math.floor((fits + tooMany) / 2);
    if (sizeOf(rows.slice(0, tried), total) <= PLANNER_RESULT_MAX_CHARS) fits = tried;
    else tooMany = tried;
  }
  return rows.slice(0, fits);
};

const sizeOf = (rows: Row[], total: number): number =>
  JSON.stringify({ total, shown: rows.length, rows, note: noteFor(total, rows.length) }).length;

const withoutEmpty = (row: Row): Row =>
  Object.fromEntries(Object.entries(row).filter(([, value]) => value !== null && value !== undefined && value !== ""));

const toMinute = (instant: unknown): unknown =>
  typeof instant === "string" ? instant.replace(/:\d{2}(\.\d+)?Z$/, "Z") : instant;

const isRow = (value: unknown): value is Row => typeof value === "object" && value !== null && !Array.isArray(value);

const personName = (person: unknown): string | undefined => {
  if (!isRow(person)) return undefined;
  const name = [person.firstName, person.lastName].filter((part) => typeof part === "string" && part).join(" ");
  return name || (typeof person.email === "string" ? person.email : undefined);
};

const text = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

const list = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === "string" && !!item);
  return typeof value === "string" ? value.split(",").map((item) => item.trim()).filter(Boolean) : [];
};

const joined = (values: string[]): string | undefined => (values.length ? values.join(",") : undefined);

const statusBuckets = (statuses: string[]): string[] =>
  Array.from(
    new Set(statuses.map((status) => STATUS_BUCKET[status as SourcingEventStatus]).filter((bucket) => !!bucket)),
  );

const withParams = (path: string, params: Record<string, string | undefined>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) search.set(key, value);
  }
  const query = search.toString();
  return query ? `${path}?${query}` : path;
};

const isNeedStatus = (status: string): boolean => (Object.values(NeedStatus) as string[]).includes(status);
